<script setup lang="ts">
// DESIGN §7 "Field, select, textarea"; B3, PLAN §4.
import { computed, useAttrs, useId } from 'vue'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  label: string
  as?: 'input' | 'select' | 'textarea'
  type?: string          // input only
  rows?: number          // textarea only
  error?: string
}>(), { as: 'input', type: 'text', rows: 3 })
const model = defineModel<string>()
const id = useId()
const errorId = `${id}-error`
// A caller's own description (a hint) is kept alongside the error's.
const attrs = useAttrs()
const describedBy = computed(
  () => [props.error ? errorId : null, attrs['aria-describedby']].filter(Boolean).join(' ') || undefined,
)

const CONTROL =
  'w-full rounded-control border bg-field px-3 text-base text-fg-primary placeholder:text-fg-secondary ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ' +
  'disabled:cursor-not-allowed disabled:bg-surface-raised disabled:text-fg-secondary'
const BORDER = { ok: 'border-border-strong', error: 'border-danger' } as const
const SHAPE = {
  input: 'h-10 max-md:min-h-touch',
  select: 'h-10 max-md:min-h-touch',
  textarea: 'py-2 leading-[1.45] resize-y',
} as const
</script>

<template>
  <div class="flex flex-col gap-1">
    <label :for="id" class="text-small font-bold text-fg-primary">{{ label }}</label>
    <input
      v-if="as === 'input'"
      v-bind="$attrs"
      :id="id"
      v-model="model"
      :type="type"
      :class="[CONTROL, error ? BORDER.error : BORDER.ok, SHAPE[as]]"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
    />
    <select
      v-else-if="as === 'select'"
      v-bind="$attrs"
      :id="id"
      v-model="model"
      :class="[CONTROL, error ? BORDER.error : BORDER.ok, SHAPE[as]]"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
    >
      <slot />
    </select>
    <textarea
      v-else
      v-bind="$attrs"
      :id="id"
      v-model="model"
      :rows="rows"
      :class="[CONTROL, error ? BORDER.error : BORDER.ok, SHAPE[as]]"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
    />
    <p v-if="error" :id="errorId" class="text-small text-danger">{{ error }}</p>
  </div>
</template>
